import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

function getCategoryName(cat: (typeof categories)[0], lang: string) {
  if (lang === 'pt') return cat.namePt;
  if (lang === 'de') return cat.nameDe;
  return cat.name;
}

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = i18n.language || 'en';
  const basePath = location.pathname.replace(/^\/(en|pt|de)/, '').replace(/\/$/, '') || '';

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    const newPath = `/${lang}${basePath ? `/${basePath}` : ''}`;
    navigate(newPath);
    setLangDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: t('nav.home'), href: `/${currentLang}`, icon: 'ri-home-4-line' },
    { label: t('nav.products'), href: `/${currentLang}/products`, icon: 'ri-shopping-bag-3-line' },
    { label: t('nav.guides'), href: `/${currentLang}/cssbuy-spreadsheet`, icon: 'ri-book-open-line' },
    { label: t('nav.review'), href: `/${currentLang}/review`, icon: 'ri-star-line' },
    { label: t('nav.shipping'), href: `/${currentLang}/shipping`, icon: 'ri-truck-line' },
    { label: t('nav.qc'), href: `/${currentLang}/qc`, icon: 'ri-camera-line' },
    { label: t('nav.safety'), href: `/${currentLang}/safety`, icon: 'ri-shield-check-line' },
  ];

  const handleNavClick = (href: string) => {
    navigate(href);
    setMobileMenuOpen(false);
    setMobileCategoriesOpen(false);
  };

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? 'shadow-sm border-b border-background-200' : 'border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between h-16">
          <a
            href={`/${currentLang}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/${currentLang}`);
            }}
            className="flex items-center gap-2 shrink-0"
          >
            <span className="font-heading text-lg md:text-xl font-bold text-primary-600">
              CSSBuy
            </span>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
              Spreadsheet
            </span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
              >
                <span>{languages.find((l) => l.code === currentLang)?.flag}</span>
                <span className="hidden sm:inline uppercase">{currentLang}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {langDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-background-200 overflow-hidden z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-50 transition-colors flex items-center gap-2 ${
                        currentLang === lang.code ? 'text-primary-600 font-medium bg-primary-50' : 'text-foreground-800'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex btn-primary text-xs py-2 px-4"
            >
              {t('buttons.buyNow')}
            </a>

            <button
              className="md:hidden p-2 text-foreground-800 hover:text-primary-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="flex items-center gap-3 text-sm font-medium text-foreground-800 hover:bg-background-50 hover:text-primary-600 py-3 px-2 rounded-lg transition-colors"
              >
                <i className={`${link.icon} text-primary-500`} />
                {link.label}
              </a>
            ))}

            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="w-full flex items-center justify-between gap-3 text-sm font-semibold text-foreground-900 py-3 px-2 rounded-lg hover:bg-background-50 transition-colors"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-primary-500" />
                {t('categories.title')}
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileCategoriesOpen && (
              <div className="pl-4 pb-2 grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      handleCategoryClick(cat.url);
                      setMobileMenuOpen(false);
                      setMobileCategoriesOpen(false);
                    }}
                    className="flex items-center gap-2 text-left text-xs font-medium text-foreground-700 hover:text-primary-600 py-2 px-2 rounded-lg hover:bg-background-50 transition-colors"
                  >
                    <i className="ri-external-link-line text-foreground-300" />
                    {getCategoryName(cat, currentLang)}
                  </button>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary w-full text-center mt-4"
            >
              {t('buttons.buyNow')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}